/**
 * RichTextEditor Component
 *
 * A simple WYSIWYG editor based on TipTap for markdown-like editing.
 * Supports basic formatting: bold, italic, code, lists, headings.
 * Converts Markdown to HTML on load and HTML to Markdown on save.
 *
 * @module components/common/RichTextEditor
 */

import { memo, useCallback, useEffect, useMemo } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import TurndownService from 'turndown';
import {
  Bold,
  Italic,
  Code,
  List,
  ListOrdered,
  Heading2,
  Quote,
  Undo,
  Redo
} from 'lucide-react';

/**
 * Simple Markdown to HTML converter
 * Handles: bold, italic, code, headings, lists, blockquotes
 */
function markdownToHtml(markdown) {
  if (!markdown) return '';

  let html = markdown
    // Escape HTML entities first
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    // Code blocks (before other processing)
    .replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>')
    // Inline code
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    // Headings
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    // Bold and italic
    .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/___(.+?)___/g, '<strong><em>$1</em></strong>')
    .replace(/__(.+?)__/g, '<strong>$1</strong>')
    .replace(/_(.+?)_/g, '<em>$1</em>')
    // Blockquotes
    .replace(/^> (.+)$/gm, '<blockquote>$1</blockquote>')
    // Unordered lists
    .replace(/^[*-] (.+)$/gm, '<li>$1</li>')
    // Ordered lists
    .replace(/^\d+\. (.+)$/gm, '<li>$1</li>')
    // Paragraphs (lines that aren't already wrapped)
    .split('\n')
    .map(line => {
      if (line.trim() === '') return '';
      if (line.startsWith('<')) return line;
      return `<p>${line}</p>`;
    })
    .join('\n')
    // Merge consecutive list items
    .replace(/(<li>.*<\/li>\n?)+/g, match => `<ul>${match}</ul>`)
    // Merge consecutive blockquotes
    .replace(/(<blockquote>.*<\/blockquote>\n?)+/g, match => {
      const content = match.replace(/<\/?blockquote>/g, '').trim();
      return `<blockquote><p>${content}</p></blockquote>`;
    });

  return html;
}

/**
 * Create TurndownService instance for HTML to Markdown conversion
 */
function createTurndownService() {
  const turndown = new TurndownService({
    headingStyle: 'atx',
    codeBlockStyle: 'fenced',
    bulletListMarker: '-'
  });
  return turndown;
}

/**
 * Toolbar button component
 */
const ToolbarButton = memo(function ToolbarButton({
  onClick,
  isActive = false,
  disabled = false,
  title,
  children
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`
        p-1.5 rounded transition-colors
        ${isActive
          ? 'bg-indigo-600 text-white'
          : 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
        }
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
      `}
    >
      {children}
    </button>
  );
});

/**
 * Toolbar separator
 */
const ToolbarSeparator = memo(function ToolbarSeparator() {
  return <div className="w-px h-5 bg-gray-700 mx-1" />;
});

/**
 * RichTextEditor Component
 *
 * @param {object} props - Component props
 * @param {string} props.value - Current content (Markdown)
 * @param {function} props.onChange - Change handler (receives Markdown string)
 * @param {string} [props.placeholder=''] - Placeholder text
 * @param {boolean} [props.disabled=false] - Whether editor is disabled
 * @param {number} [props.minHeight=120] - Minimum height in pixels
 * @param {string} [props.className=''] - Additional CSS classes
 * @returns {React.ReactElement} Editor element
 */
const RichTextEditor = memo(function RichTextEditor({
  value = '',
  onChange,
  placeholder = 'Enter text...',
  disabled = false,
  minHeight = 120,
  className = ''
}) {
  // Create turndown service for HTML to Markdown conversion
  const turndown = useMemo(() => createTurndownService(), []);

  // Convert initial Markdown value to HTML
  const initialHtml = useMemo(() => markdownToHtml(value), []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [2, 3]
        }
      }),
      Placeholder.configure({
        placeholder
      })
    ],
    content: initialHtml,
    editable: !disabled,
    onUpdate: ({ editor }) => {
      // Convert HTML back to Markdown before calling onChange
      const html = editor.getHTML();
      const markdown = turndown.turndown(html);
      onChange?.(markdown);
    }
  });

  // Update content when value changes externally
  useEffect(() => {
    if (editor) {
      const currentMarkdown = turndown.turndown(editor.getHTML());
      if (value !== currentMarkdown) {
        const newHtml = markdownToHtml(value);
        editor.commands.setContent(newHtml, false);
      }
    }
  }, [editor, value, turndown]);

  // Update editable state
  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [editor, disabled]);

  if (!editor) {
    return null;
  }

  return (
    <div className={`border border-gray-700 rounded-lg overflow-hidden ${className}`}>
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 p-1.5 border-b border-gray-700 bg-gray-800/50">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          disabled={disabled}
          title="Bold (Ctrl+B)"
        >
          <Bold className="w-4 h-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          disabled={disabled}
          title="Italic (Ctrl+I)"
        >
          <Italic className="w-4 h-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleCode().run()}
          isActive={editor.isActive('code')}
          disabled={disabled}
          title="Inline Code"
        >
          <Code className="w-4 h-4" />
        </ToolbarButton>

        <ToolbarSeparator />

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          isActive={editor.isActive('heading', { level: 2 })}
          disabled={disabled}
          title="Heading"
        >
          <Heading2 className="w-4 h-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive('bulletList')}
          disabled={disabled}
          title="Bullet List"
        >
          <List className="w-4 h-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive('orderedList')}
          disabled={disabled}
          title="Numbered List"
        >
          <ListOrdered className="w-4 h-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          isActive={editor.isActive('blockquote')}
          disabled={disabled}
          title="Quote"
        >
          <Quote className="w-4 h-4" />
        </ToolbarButton>

        <ToolbarSeparator />

        <ToolbarButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={disabled || !editor.can().undo()}
          title="Undo (Ctrl+Z)"
        >
          <Undo className="w-4 h-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={disabled || !editor.can().redo()}
          title="Redo (Ctrl+Shift+Z)"
        >
          <Redo className="w-4 h-4" />
        </ToolbarButton>
      </div>

      {/* Editor Content */}
      <EditorContent
        editor={editor}
        className="prose prose-invert prose-sm max-w-none"
        style={{ minHeight }}
      />

      <style>{`
        .ProseMirror {
          padding: 0.75rem;
          outline: none;
          min-height: ${minHeight}px;
        }
        .ProseMirror p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: #6b7280;
          pointer-events: none;
          height: 0;
        }
        .ProseMirror h2 {
          font-size: 1.25rem;
          font-weight: 600;
          margin-top: 1rem;
          margin-bottom: 0.5rem;
        }
        .ProseMirror h3 {
          font-size: 1.1rem;
          font-weight: 600;
          margin-top: 0.75rem;
          margin-bottom: 0.5rem;
        }
        .ProseMirror ul, .ProseMirror ol {
          padding-left: 1.5rem;
          margin: 0.5rem 0;
        }
        .ProseMirror li {
          margin: 0.25rem 0;
        }
        .ProseMirror blockquote {
          border-left: 3px solid #4f46e5;
          padding-left: 1rem;
          margin: 0.5rem 0;
          color: #9ca3af;
        }
        .ProseMirror code {
          background: #374151;
          padding: 0.125rem 0.375rem;
          border-radius: 0.25rem;
          font-size: 0.875rem;
        }
        .ProseMirror pre {
          background: #1f2937;
          padding: 0.75rem;
          border-radius: 0.375rem;
          margin: 0.5rem 0;
          overflow-x: auto;
        }
        .ProseMirror pre code {
          background: none;
          padding: 0;
        }
      `}</style>
    </div>
  );
});

export default RichTextEditor;
